import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { User, Mail, Phone, MessageSquare } from 'lucide-react';
import { fetchGeolocation, submitLead, getCountries, findCountryByCode } from '../services/leadService';
import { validateEmail, validatePhoneNumber, validateName } from '../utils/validation';

const ContactForm = ({ variant = 'default' }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '1',
    country: 'US',
    phone: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoLocationData, setGeoLocationData] = useState(null);
  const [countries] = useState(getCountries());
  
  // Fetch geolocation on component mount
  useEffect(() => {
    const loadGeolocation = async () => {
      const geoData = await fetchGeolocation();
      if (geoData && geoData.isSuccess && geoData.data) {
        setGeoLocationData(geoData);
        
        // Auto-fill country based on geolocation
        const detectedCountry = findCountryByCode(geoData.data.countryCode);
        if (detectedCountry) {
          setFormData(prev => ({
            ...prev,
            country: detectedCountry.code,
            countryCode: detectedCountry.dialCode
          }));
        }
      }
    };
    
    loadGeolocation();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    
    // Validate name
    const nameValidation = validateName(formData.name);
    if (!nameValidation.isValid) {
      newErrors.name = nameValidation.error;
    }
    
    // Validate email
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.error;
    }
    
    // Validate phone with country code
    const phoneValidation = validatePhoneNumber(formData.phone, formData.countryCode);
    if (!phoneValidation.isValid) {
      newErrors.phone = phoneValidation.error;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Submit lead using centralized API service
      const result = await submitLead(
        {
          name: formData.name,
          email: formData.email,
          phoneNumber: formData.phone,
          countryDialCode: formData.countryCode,
          country: formData.country,
          description: formData.description
        },
        geoLocationData
      );

      if (result.success && result.customerId) {
        // Redirect to application page with customer ID
        toast({
          title: 'Success!',
          description: result.message || 'Thank you for your interest.',
          variant: 'default'
        });
        
        // Redirect after short delay
        setTimeout(() => {
          window.location.href = `/application/main?customerid=${result.customerId}`;
        }, 1000);
      } else {
        // Show error message
        toast({
          title: 'Error',
          description: result.error || 'Something went wrong. Please try again.',
          variant: 'destructive'
        });
        setIsSubmitting(false);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Network error. Please check your connection and try again.',
        variant: 'destructive'
      });
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCountryChange = (e) => {
    const selectedCode = e.target.value;
    const country = countries.find(c => c.dialCode === selectedCode);
    if (country) {
      setFormData(prev => ({
        ...prev,
        countryCode: country.dialCode,
        country: country.code
      }));
    }
  };

  const compactStyle = variant === 'compact';

  return (
    <form onSubmit={handleSubmit} className={`space-y-4 ${compactStyle ? '' : 'max-w-xl'}`}>
      {/* Name Field */}
      <div className="space-y-1.5">
        <Label htmlFor="name" className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
          <User className="w-3.5 h-3.5 text-primary" />
          Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="name"
          type="text"
          name="name"
          placeholder="Enter your full name"
          value={formData.name}
          onChange={handleChange}
          className={`h-10 pl-3 pr-3 text-sm transition-all ${
            errors.name 
              ? 'border-red-500 focus:ring-red-500' 
              : 'border-gray-300 focus:ring-primary focus:border-primary'
          }`}
        />
        {errors.name && (
          <p className="text-red-500 text-xs flex items-center gap-1 mt-1">
            <span className="text-xs">⚠</span> {errors.name}
          </p>
        )}
      </div>

      {/* Email Field */}
      <div className="space-y-1.5">
        <Label htmlFor="email" className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5 text-primary" />
          Email <span className="text-red-500">*</span>
        </Label>
        <Input
          id="email"
          type="email"
          name="email"
          placeholder="Enter your email address"
          value={formData.email}
          onChange={handleChange}
          className={`h-10 pl-3 pr-3 text-sm transition-all ${
            errors.email 
              ? 'border-red-500 focus:ring-red-500' 
              : 'border-gray-300 focus:ring-primary focus:border-primary'
          }`}
        />
        {errors.email && (
          <p className="text-red-500 text-xs flex items-center gap-1 mt-1">
            <span className="text-xs">⚠</span> {errors.email}
          </p>
        )}
      </div>

      {/* Contact Number with Country Code */}
      <div className="space-y-1.5">
        <Label htmlFor="phone" className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
          <Phone className="w-3.5 h-3.5 text-primary" />
          Contact Number <span className="text-red-500">*</span>
        </Label>
        <div className="flex gap-2">
          <select
            name="countryCode"
            value={formData.countryCode}
            onChange={handleCountryChange}
            className="h-10 px-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-white text-sm font-medium transition-all cursor-pointer hover:border-primary/60"
            title={countries.find(c => c.dialCode === formData.countryCode)?.name || ''}
          >
            {countries.map(country => (
              <option key={country.code} value={country.dialCode}>
                +{country.dialCode} ({country.code})
              </option>
            ))}
          </select>
          <div className="flex-1">
            <Input
              id="phone"
              type="tel"
              name="phone"
              placeholder="Enter phone number"
              value={formData.phone}
              onChange={handleChange}
              className={`h-10 pl-3 pr-3 text-sm transition-all ${
                errors.phone 
                  ? 'border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:ring-primary focus:border-primary'
              }`}
            />
          </div>
        </div>
        {errors.phone && (
          <p className="text-red-500 text-xs flex items-center gap-1 mt-1">
            <span className="text-xs">⚠</span> {errors.phone}
          </p>
        )}
      </div>

      {/* Description Field */}
      <div className="space-y-1.5">
        <Label htmlFor="description" className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
          <MessageSquare className="w-3.5 h-3.5 text-primary" />
          Description
        </Label>
        <Textarea
          id="description"
          name="description"
          placeholder="Describe your requirements..."
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className="pl-3 pr-3 pt-2 text-sm resize-none transition-all border-gray-300 focus:ring-primary focus:border-primary"
        />
        <p className="text-xs text-gray-400 text-right mt-1">
          {formData.description.length} characters
        </p>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full h-11 bg-secondary hover:bg-secondary/90 text-white text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] mt-5"
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <span className="animate-spin">⏳</span>
            Submitting...
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            Get Started
            <span className="text-lg">→</span>
          </span>
        )}
      </Button>

      <p className="text-xs text-center text-gray-400 mt-2">
        By submitting, you agree to our Terms & Privacy Policy
      </p>
    </form>
  );
};

export default ContactForm;
